<div class="parking-right-list">
    <!-- Header with Add Button -->
    <div class="flex flex-wrap justify-between items-center gap-2 mb-4">
        <div class="flex items-center gap-2">
            <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-blue-600">verified_user</mat-icon>
            <h3 class="text-sm font-semibold text-slate-900">{{ 'parkingRight.list.title' | translate }}</h3>
            <span class="text-xs font-medium text-slate-400">({{ totalCount() }})</span>
        </div>
        <div class="flex flex-wrap gap-2">
            @if (isMappingAllowed()) {
            <button type="button" (click)="openMappingDialog()"
                [matTooltip]="'parkingRight.list.manageSyncTooltip' | translate"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors px-3 py-1.5 rounded-lg">
                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">sync_alt</mat-icon>
                {{ 'parkingRight.list.oldDbSyncMapping' | translate }}
            </button>
            }
            @if (isMappingAllowed()) {
            <button type="button" (click)="openPoolCapBackfillDialog()"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors px-3 py-1.5 rounded-lg">
                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">directions_car</mat-icon>
                {{ 'parkingRight.list.poolCapBackfill' | translate }}
            </button>
            }
            @if(isImportAllowed()){
            <button type="button" (click)="openImportDialog()" [disabled]="!isImportAllowed()"
                [matTooltip]="isImportAllowed() ? ('parkingRight.list.importTooltip' | translate) : ''"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">upload</mat-icon>
                {{ 'parkingRight.list.importCsv' | translate }}
            </button>
            }
            @if (!isPoolParkingSpace()) {
            <button type="button" (click)="openAddDialog()"
                class="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors px-3 py-1.5 rounded-lg">
                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">add</mat-icon>
                {{ 'parkingRight.list.addRight' | translate }}
            </button>
            }
        </div>
    </div>

    <!-- Search Filter -->
    <div class="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
        <div class="flex flex-col sm:flex-row sm:items-end gap-3">
            <!-- Name / comment search (before the plate box) -->
            <div class="flex flex-col gap-1 flex-1 max-w-xs">
                <label class="text-xs font-semibold text-slate-500">{{ 'parkingRight.list.nameSearch' | translate
                    }}</label>
                <mat-form-field appearance="fill" class="w-full filter-form-field">
                    <mat-icon matPrefix class="text-gray-400 text-base">search</mat-icon>
                    <input matInput [placeholder]="'parkingRight.list.nameSearchPlaceholder' | translate"
                        [ngModel]="searchTerm()" (ngModelChange)="searchTerm.set($event)"
                        (keyup.enter)="applySearchFilters()" />
                </mat-form-field>
            </div>

            <!-- License Plate Autocomplete -->
            <div class="flex flex-col gap-1 flex-1 max-w-xs">
                <label class="text-xs font-semibold text-slate-500">{{ 'parkingRight.list.lpFilter' | translate
                    }}</label>
                <mat-form-field appearance="fill" class="w-full filter-form-field">
                    <mat-icon matPrefix class="text-gray-400 text-base">badge</mat-icon>
                    <input matInput [placeholder]="'parkingRight.list.searchPlate' | translate"
                        [ngModel]="lpSearchTerm()" (ngModelChange)="onLpSearchChange($event)"
                        [matAutocomplete]="lpAuto" />
                    @if (isLoadingLps()) {
                    <mat-spinner matSuffix diameter="16"></mat-spinner>
                    }
                    <mat-autocomplete #lpAuto [displayWith]="displayLp"
                        (optionSelected)="onLpSelected($event.option.value)">
                        @for (lp of filteredLpOptions(); track lp.id) {
                        <mat-option [value]="lp">
                            <span class="font-medium">{{ lp.fullNumber }}</span>
                            @if (lp.parkersIDs && lp.parkersIDs.length > 0) {
                            <span class="text-xs text-gray-500 ml-2">({{ lp.parkersIDs.length }} parker{{
                                lp.parkersIDs.length > 1 ? 's' : '' }})</span>
                            }
                        </mat-option>
                        }
                    </mat-autocomplete>
                </mat-form-field>
            </div>

            <!-- Parking Type Definition filter -->
            <div class="flex flex-col gap-1 flex-1 max-w-xs">
                <label class="text-xs font-semibold text-slate-500">{{ 'parkingRight.list.parkingTypeDefinition' |
                    translate
                    }}</label>
                <mat-form-field appearance="fill" class="w-full filter-form-field">
                    <mat-icon matPrefix class="text-gray-400 text-base">category</mat-icon>
                    <mat-select [ngModel]="selectedParkingTypeDefinitionId()"
                        (ngModelChange)="onParkingTypeFilterChange($event)"
                        [placeholder]="'parkingRight.list.allTypes' | translate">
                        <mat-option [value]="null">{{ 'parkingRight.list.allTypes' | translate }}</mat-option>
                        @for (option of parkingTypeDefinitions(); track option.id) {
                        <mat-option [value]="option.id">{{ option.name }}</mat-option>
                        }
                    </mat-select>
                </mat-form-field>
            </div>

            <!-- Status filter -->
            <div class="flex flex-col gap-1 w-40">
                <label class="text-xs font-semibold text-slate-500">{{ 'parkingRight.list.status' | translate }}</label>
                <mat-form-field appearance="fill" class="w-full filter-form-field">
                    <mat-icon matPrefix class="text-gray-400 text-base">toggle_on</mat-icon>
                    <mat-select [ngModel]="selectedStatus()" (ngModelChange)="onStatusFilterChange($event)">
                        <mat-option value="all">{{ 'parkingRight.list.allStatuses' | translate }}</mat-option>
                        <mat-option value="active">{{ 'common.active' | translate }}</mat-option>
                        <mat-option value="inactive">{{ 'common.inactive' | translate }}</mat-option>
                    </mat-select>
                </mat-form-field>
            </div>

            <div class="flex gap-2 pb-[1.34375em]">
                <button type="button" (click)="applySearchFilters()" [disabled]="isLoading()"
                    class="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors px-4 py-2 rounded-lg disabled:opacity-50">
                    <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">search</mat-icon>
                    {{ 'parkingRight.list.search' | translate }}
                </button>
                <button type="button" (click)="clearFilters()" [disabled]="isLoading() || !hasActiveFilters()"
                    class="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">clear</mat-icon>
                    {{ 'parkingRight.list.clear' | translate }}
                </button>
            </div>
        </div>
    </div>

    <!-- Loading Skeleton -->
    @if (isLoading()) {
    <div class="skeleton-table rounded-lg border border-gray-200 overflow-hidden" aria-busy="true"
        [attr.aria-label]="'parkingRight.list.loadingAria' | translate">
        <div class="skeleton-row skeleton-row--head">
            @for (cell of [1,2,3,4,5,6]; track cell) {
            <div class="skeleton-bar skeleton-bar--head"></div>
            }
        </div>
        @for (row of skeletonRows; track $index) {
        <div class="skeleton-row">
            @for (cell of [1,2,3,4,5,6]; track cell) {
            <div class="skeleton-bar" [style.width.%]="cell === 1 ? 60 : (cell === 6 ? 40 : 80)"></div>
            }
        </div>
        }
    </div>
    } @else if (parkingRights().length === 0) {
    <div class="bg-slate-50 border border-slate-100 rounded-xl p-8 text-center">
        <mat-icon class="text-slate-300" style="font-size:44px;width:44px;height:44px;">verified_user</mat-icon>
        <p class="text-sm text-slate-600 mt-2">{{ 'parkingRight.list.empty' | translate }}</p>
        <p class="text-xs text-slate-400 mt-1">{{ 'parkingRight.list.emptyHint' | translate }}</p>
    </div>
    } @else {
    <!-- Table with Expandable Rows -->
    <div class="rights-table-wrap rounded-xl border border-slate-200 overflow-hidden">
        <table mat-table [dataSource]="dataSource" matSort (matSortChange)="onSortChange($event)" multiTemplateDataRows
            class="w-full">

            <!-- Expand Column -->
            <ng-container matColumnDef="expand">
                <th mat-header-cell *matHeaderCellDef class="w-12"></th>
                <td mat-cell *matCellDef="let right">
                    <button mat-icon-button (click)="toggleRow(right); $event.stopPropagation()">
                        <mat-icon class="transition-transform duration-200"
                            [class.rotate-180]="expandedElement === right">
                            expand_more
                        </mat-icon>
                    </button>
                </td>
            </ng-container>

            <!-- Name Column -->
            <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'parkingRight.list.name' | translate }}</th>
                <td mat-cell *matCellDef="let right">
                    <span class="font-medium">{{ right.name || ('parkingRight.list.unnamed' | translate) }}</span>
                </td>
            </ng-container>

            <!-- Parking Type Definition Column -->
            <ng-container matColumnDef="parkingType">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'parkingRight.list.parkingTypeDefinition' |
                    translate }}
                </th>
                <td mat-cell *matCellDef="let right">
                    <div class="flex items-center gap-2">
                        <mat-icon class="text-purple-600 text-base">category</mat-icon>
                        <span class="font-medium">{{ getParkingTypeName(right) }}</span>
                    </div>
                </td>
            </ng-container>

            <!-- Validity Column (Combined) -->
            <ng-container matColumnDef="validity">
                <th mat-header-cell *matHeaderCellDef>{{ 'parkingRight.list.validity' | translate }}</th>
                <td mat-cell *matCellDef="let right">
                    <div class="text-sm">
                        @if (getValidFrom(right) || getValidTo(right)) {
                        <span class="text-gray-600">
                            {{ getValidFrom(right) | date: 'mediumDate' : '' : '' }}
                            <mat-icon class="text-xs align-middle mx-1">arrow_forward</mat-icon>
                            {{ getValidTo(right) ? (getValidTo(right) | date: 'mediumDate') :
                            ('parkingRight.list.noExpiry' | translate) }}
                        </span>
                        } @else {
                        <span class="text-gray-500 italic">{{ 'parkingRight.list.alwaysValid' | translate }}</span>
                        }
                    </div>
                </td>
            </ng-container>

            <!-- License Plates Summary -->
            <ng-container matColumnDef="licensePlates">
                <th mat-header-cell *matHeaderCellDef>{{ 'parkingRight.list.licensePlates' | translate }}</th>
                <td mat-cell *matCellDef="let right">
                    <div class="flex items-center gap-1">
                        <mat-icon class="text-base text-gray-500">directions_car</mat-icon>
                        @if (getLicensePlateNumbers(right).length <= 2) { <span class="text-gray-700">{{
                            getLicensePlateNumbers(right).join(', ') }}</span>
                            } @else {
                            <span class="text-gray-700">{{ getLicensePlateNumbers(right)[0] }}, {{
                                getLicensePlateNumbers(right)[1] }}</span>
                            <span class="text-gray-500 text-xs">+{{ getLicensePlateNumbers(right).length - 2 }}
                                {{ 'parkingRight.list.moreSuffix' | translate }}</span>
                            }
                    </div>
                </td>
            </ng-container>

            <!-- Zones Summary -->
            <ng-container matColumnDef="zones">
                <th mat-header-cell *matHeaderCellDef>{{ 'parkingRight.list.zones' | translate }}</th>
                <td mat-cell *matCellDef="let right">
                    <div class="flex items-center gap-1">
                        <mat-icon class="text-base text-gray-500">location_on</mat-icon>
                        <span class="text-gray-700">{{ getZones(right).length }} {{ 'parkingRight.list.zonesSuffix' |
                            translate }}</span>
                    </div>
                </td>
            </ng-container>

            <!-- Is Active Column -->
            <ng-container matColumnDef="isActive">
                <th mat-header-cell *matHeaderCellDef>{{ 'parkingRight.list.status' | translate }}</th>
                <td mat-cell *matCellDef="let right">
                    <jjames-badge [variant]="right.isActive ? 'success' : 'danger'">
                        {{ (right.isActive ? 'common.active' : 'common.inactive') | translate }}
                    </jjames-badge>
                </td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef class="w-24">{{ 'parkingRight.list.actions' | translate }}</th>
                <td mat-cell *matCellDef="let right">
                    <div class="flex gap-1">
                        <button mat-icon-button color="primary"
                            (click)="openEditDialog(right); $event.stopPropagation()"
                            [attr.aria-label]="'parkingRight.list.editAria' | translate">
                            <mat-icon>edit</mat-icon>
                        </button>
                        @if(!isPoolParking(right)) {
                        <button mat-icon-button color="warn"
                            (click)="deleteParkingRight(right); $event.stopPropagation()"
                            [attr.aria-label]="'parkingRight.list.deleteAria' | translate"
                            [disabled]="isPoolParking(right)">
                            <mat-icon>delete</mat-icon>
                        </button>
                        }
                    </div>
                </td>
            </ng-container>

            <!-- Expanded Detail Row -->
            <ng-container matColumnDef="expandedDetail">
                <td mat-cell *matCellDef="let right" [attr.colspan]="displayedColumns.length">
                    <div class="expanded-detail" [class.expanded]="right === expandedElement">
                        <div class="py-4 px-6 bg-slate-50 border-t border-slate-200">
                            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

                                <!-- Type-Specific Details -->
                                <div class="space-y-3">
                                    <h4 class="font-semibold text-gray-800 flex items-center gap-2">
                                        @if (isTimeBased(right)) {
                                        <mat-icon class="text-blue-500">schedule</mat-icon>
                                        <span>{{ 'parkingRight.list.timeBasedDetails' | translate }}</span>
                                        } @else {
                                        <mat-icon class="text-green-500">timer</mat-icon>
                                        <span>{{ 'parkingRight.list.durationBasedDetails' | translate }}</span>
                                        }
                                    </h4>

                                    @if (isTimeBased(right)) {
                                    <div class="space-y-2 text-sm">
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-500 w-24">{{ 'parkingRight.list.timeLbl' | translate
                                                }}</span>
                                            <span class="font-medium">{{ getTimeRange(right) }}</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-500 w-24">{{ 'parkingRight.list.daysLbl' | translate
                                                }}</span>
                                            <span class="font-medium">{{ getValidDays(right) }}</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-500 w-24">{{ 'parkingRight.list.recurringLbl' |
                                                translate }}</span>
                                            <jjames-badge [variant]="isRecurring(right) ? 'info' : 'secondary'"
                                                size="sm">
                                                {{ (isRecurring(right) ? 'parkingRight.list.yes' :
                                                'parkingRight.list.no') | translate }}
                                            </jjames-badge>
                                        </div>
                                    </div>
                                    } @else if (isDurationBased(right)) {
                                    <div class="space-y-2 text-sm">
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-500 w-24">{{ 'parkingRight.list.durationLbl' |
                                                translate }}</span>
                                            <span class="font-medium">{{ getDurationText(right) }}</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-500 w-24">{{ 'parkingRight.list.renewableLbl' |
                                                translate }}</span>
                                            <jjames-badge [variant]="isRenewable(right) ? 'success' : 'secondary'"
                                                size="sm">
                                                {{ (isRenewable(right) ? 'parkingRight.list.yes' :
                                                'parkingRight.list.no') | translate }}
                                            </jjames-badge>
                                        </div>
                                        @if (isRenewable(right)) {
                                        <div class="flex items-center gap-2">
                                            <span class="text-gray-500 w-24">{{ 'parkingRight.list.maxRenewalsLbl' |
                                                translate }}</span>
                                            <span class="font-medium">{{ getMaxRenewals(right) }}</span>
                                        </div>
                                        }
                                    </div>
                                    }
                                </div>

                                <!-- License Plates -->
                                <div class="space-y-3">
                                    <h4 class="font-semibold text-gray-800 flex items-center gap-2">
                                        <mat-icon class="text-gray-600">directions_car</mat-icon>
                                        <span>{{ 'parkingRight.list.licensePlates' | translate }} ({{
                                            getLicensePlateNumbers(right).length }})</span>
                                    </h4>
                                    <div class="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                        @for (lp of getLicensePlateNumbers(right); track lp) {
                                        <jjames-badge [variant]="'secondary'">
                                            <mat-icon class="text-xs mr-1">badge</mat-icon>
                                            {{ lp }}
                                        </jjames-badge>
                                        } @empty {
                                        <span class="text-gray-500 text-sm italic">{{
                                            'parkingRight.list.noLicensePlates' | translate }}</span>
                                        }
                                    </div>
                                </div>

                                <!-- Zones -->
                                <div class="space-y-3">
                                    <h4 class="font-semibold text-gray-800 flex items-center gap-2">
                                        <mat-icon class="text-gray-600">location_on</mat-icon>
                                        <span>{{ 'parkingRight.list.parkingZones' | translate }}</span>
                                    </h4>
                                    <div class="flex flex-wrap gap-2">
                                        @for (zone of getZones(right); track zone) {
                                        <jjames-badge [variant]="'info'">
                                            <mat-icon class="text-xs mr-1">place</mat-icon>
                                            {{ zone }}
                                        </jjames-badge>
                                        } @empty {
                                        <span class="text-gray-500 text-sm italic">{{
                                            'parkingRight.list.allZonesAllowed' | translate }}</span>
                                        }
                                    </div>
                                </div>

                            </div>

                            <!-- Metadata footer -->
                            <div class="mt-5 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div class="meta-cell">
                                    <span class="meta-cell__label">
                                        <mat-icon class="text-gray-400">event</mat-icon> {{ 'parkingRight.list.created'
                                        | translate }}
                                    </span>
                                    <span class="meta-cell__value">
                                        {{ right.createdAt ? (right.createdAt | date: 'medium') : 'N/A' }}
                                    </span>
                                </div>
                                <div class="meta-cell">
                                    <span class="meta-cell__label">
                                        <mat-icon class="text-gray-400">update</mat-icon> {{
                                        'parkingRight.list.lastUpdated' | translate }}
                                    </span>
                                    <span class="meta-cell__value">
                                        {{ right.updatedAt ? (right.updatedAt | date: 'medium') : 'N/A' }}
                                    </span>
                                </div>
                                <div class="meta-cell">
                                    <span class="meta-cell__label">
                                        <mat-icon class="text-gray-400">event_available</mat-icon> {{
                                        'parkingRight.list.validity' | translate }}
                                    </span>
                                    <span class="meta-cell__value">
                                        @if (getValidFrom(right) || getValidTo(right)) {
                                        {{ getValidFrom(right) | date: 'mediumDate' : '' : '' }}
                                        <mat-icon class="text-xs align-middle mx-1">arrow_forward</mat-icon>
                                        {{ getValidTo(right) ? (getValidTo(right) | date: 'mediumDate') :
                                        ('parkingRight.list.noExpiry' | translate) }}
                                        } @else {
                                        <span class="italic">{{ 'parkingRight.list.alwaysValid' | translate }}</span>
                                        }
                                    </span>
                                </div>
                                <div class="meta-cell">
                                    <span class="meta-cell__label">
                                        <mat-icon class="text-gray-400">notes</mat-icon> {{ 'parkingRight.list.comment'
                                        | translate }}
                                    </span>
                                    <span class="meta-cell__value">{{ right.comment || '—' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"
                class="cursor-pointer hover:bg-slate-50 transition-colors" [class.bg-blue-50]="expandedElement === row"
                (click)="toggleRow(row)">
            </tr>
            <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
        </table>

        <mat-paginator [length]="totalCount()" [pageSize]="pageSize()" [pageIndex]="pageIndex()"
            [pageSizeOptions]="[10, 25,100,1000 ]" (page)="onPageChange($event)" showFirstLastButtons>
        </mat-paginator>
    </div>
    }
</div>

<!-- Delete Confirmation Modal -->
<jjames-modal [isOpen]="deleteConfirmOpen()" [title]="'parkingRight.list.deleteTitle' | translate" size="sm"
    (closed)="cancelDelete()">
    <div class="flex flex-col gap-4">
        <div class="flex items-start gap-3">
            <mat-icon class="text-red-500 mt-0.5">warning</mat-icon>
            <p class="text-gray-700">{{ 'parkingRight.list.deleteConfirm' | translate }}</p>
        </div>
        <div class="flex justify-end gap-2">
            <jjames-button [variant]="'secondary'" (clicked)="cancelDelete()">{{ 'parkingRight.list.cancel' | translate
                }}</jjames-button>
            <jjames-button [variant]="'danger'" matIconLeft="delete" (clicked)="confirmDelete()">{{
                'parkingRight.list.delete' | translate }}</jjames-button>
        </div>
    </div>
</jjames-modal>